import { useCallback, useMemo, useState } from 'react'

import type {
  GridSelection,
  Row,
  TimeGridEntity,
  TimeGridEvent,
  TimeSlot,
} from 'modules/shift_management/components/ShiftManagement/TimeGridTable/types'
import {
  fromCellKey,
  getColumnCellCoords,
  getRowCellCoords,
  toCellKey,
} from 'modules/shift_management/utils/TimeGridTable/cellCoordinates'

interface UseGridSelectionProps<TEntity extends TimeGridEntity, TEvent extends TimeGridEvent> {
  rows: Row<TEntity, TEvent>[]
  slots: TimeSlot[]
  rowIndexByEntityId: Map<string, number>
  isCellSelectable?: (entity: TEntity, slot: TimeSlot) => boolean
}

function useGridSelection<TEntity extends TimeGridEntity, TEvent extends TimeGridEvent>({
  rows,
  slots,
  rowIndexByEntityId,
  isCellSelectable,
}: UseGridSelectionProps<TEntity, TEvent>): {
  selection: GridSelection
  selectedCellKeys: Set<string>
  selectedRowId: string | null
  selectedColumnIndex: number | null
  selectedFocusedCell: { entityId: string; slotIndex: number } | null
  selectedEventKeys: Set<string>
  clearSelection: () => void
  handleCellSelected: (
    entity: TEntity,
    slotIndex: number,
    modifiers: { shiftKey: boolean; metaKey: boolean; ctrlKey: boolean }
  ) => void
  handleRowHeaderSelected: (entity: TEntity) => void
  handleColumnHeaderSelected: (columnIndex: number) => void
  handleEventSelected: (
    entityId: string,
    eventId: string,
    modifiers: { shiftKey: boolean; metaKey: boolean; ctrlKey: boolean }
  ) => void
  handleCornerCellSelected: () => void
  isAllSelected: boolean
  fromCellKey: (key: string) => { entityId: string; slotIndex: number }
} {
  const [selection, setSelection] = useState<GridSelection>({ kind: 'none' })

  const checkSelectable = useCallback(
    (entityId: string): boolean => {
      const rowIndex = rowIndexByEntityId.get(entityId)
      if (rowIndex === undefined) return false
      const row = rows[rowIndex]
      return row?.entity.selectable !== false
    },
    [rows, rowIndexByEntityId]
  )

  const checkCellSelectable = useCallback(
    (entityId: string, slotIndex: number): boolean => {
      if (!checkSelectable(entityId)) return false

      const rowIndex = rowIndexByEntityId.get(entityId)
      if (rowIndex === undefined) return false
      const row = rows[rowIndex]
      const slot = slots[slotIndex]

      if (!row || !slot) return false
      if (!isCellSelectable) return true

      return isCellSelectable(row.entity, slot)
    },
    [checkSelectable, isCellSelectable, rowIndexByEntityId, rows, slots]
  )

  const selectedCellKeys = useMemo(() => {
    return selection.kind === 'cells' ? new Set(selection.keys) : new Set<string>()
  }, [selection])

  const selectedRowId = selection.kind === 'row' ? selection.rowId : null
  const selectedColumnIndex = selection.kind === 'column' ? selection.columnIndex : null

  const selectedFocusedCell = useMemo(() => {
    if (selection.kind !== 'cells') return null
    return selection.focused
  }, [selection])

  const clearSelection = useCallback(() => {
    setSelection({ kind: 'none' })
  }, [])

  const selectColumn = useCallback((columnIndex: number) => {
    setSelection({ kind: 'column', columnIndex })
  }, [])

  const selectColumnCellsExcept = useCallback(
    (columnIndex: number, excludedRowId: string) => {
      const cells = getColumnCellCoords(columnIndex, rows).filter(
        (cell) =>
          cell.entityId !== excludedRowId && checkCellSelectable(cell.entityId, cell.slotIndex)
      )
      const next = new Set(cells.map((c) => toCellKey(c.entityId, c.slotIndex)))

      if (next.size === 0) {
        setSelection({ kind: 'none' })
        return
      }

      const fallbackRow = rows[0]?.entity.id
      const focusedRowId =
        fallbackRow && fallbackRow !== excludedRowId ? fallbackRow : rows[1]?.entity.id
      const firstSelectedKey = Array.from(next)[0]
      if (!firstSelectedKey) {
        setSelection({ kind: 'none' })
        return
      }
      const focusedKey = focusedRowId ? toCellKey(focusedRowId, columnIndex) : firstSelectedKey
      const focused = fromCellKey(focusedKey)
      setSelection({ kind: 'cells', keys: next, anchor: focused, focused })
    },
    [rows, checkCellSelectable]
  )

  const selectSingleCell = useCallback((cell: { entityId: string; slotIndex: number }) => {
    const key = toCellKey(cell.entityId, cell.slotIndex)
    setSelection({ kind: 'cells', keys: new Set([key]), anchor: cell, focused: cell })
  }, [])

  const toggleCell = useCallback((cell: { entityId: string; slotIndex: number }) => {
    const key = toCellKey(cell.entityId, cell.slotIndex)
    setSelection((prev) => {
      if (prev.kind !== 'cells') {
        return { kind: 'cells', keys: new Set([key]), anchor: cell, focused: cell }
      }

      const next = new Set(prev.keys)
      const wasSelected = next.has(key)
      // Meta-click toggles the clicked cell. If it was already selected, remove it.
      if (wasSelected) {
        next.delete(key)
      } else {
        next.add(key)
      }

      if (next.size === 0) {
        return { kind: 'none' }
      }
      const focused = cell
      let anchor = prev.anchor

      const anchorKey = toCellKey(anchor.entityId, anchor.slotIndex)

      // Keep anchor inside the selection; clipboard offsets rely on this invariant.
      if (!next.has(anchorKey)) {
        const focusedKey = toCellKey(focused.entityId, focused.slotIndex)

        const fallbackKey = next.has(focusedKey) ? focusedKey : Array.from(next)[0]

        if (!fallbackKey) {
          // should be unreachable because keys.size > 0
          return { kind: 'none' }
        }
        anchor = fromCellKey(fallbackKey)
      }

      return {
        kind: 'cells',
        keys: next,
        anchor,
        focused,
      }
    })
  }, [])

  const addCellRange = useCallback(
    (
      anchorCell: { entityId: string; slotIndex: number },
      targetCell: { entityId: string; slotIndex: number },
      append: boolean
    ) => {
      const anchorRowIndex = rowIndexByEntityId.get(anchorCell.entityId)
      const targetRowIndex = rowIndexByEntityId.get(targetCell.entityId)

      if (anchorRowIndex === undefined || targetRowIndex === undefined) return

      const minRowIndex = Math.min(anchorRowIndex, targetRowIndex)
      const maxRowIndex = Math.max(anchorRowIndex, targetRowIndex)
      const minSlotIndex = Math.min(anchorCell.slotIndex, targetCell.slotIndex)
      const maxSlotIndex = Math.max(anchorCell.slotIndex, targetCell.slotIndex)

      setSelection((prev) => {
        const next = append && prev.kind === 'cells' ? new Set(prev.keys) : new Set<string>()

        for (let rowIndex = minRowIndex; rowIndex <= maxRowIndex; rowIndex += 1) {
          const row = rows[rowIndex]
          if (!row) continue

          for (let slotIndex = minSlotIndex; slotIndex <= maxSlotIndex; slotIndex += 1) {
            if (!checkCellSelectable(row.entity.id, slotIndex)) continue
            next.add(toCellKey(row.entity.id, slotIndex))
          }
        }

        if (next.size === 0) return { kind: 'none' }

        return {
          kind: 'cells',
          keys: next,
          anchor: anchorCell,
          focused: targetCell,
        }
      })
    },
    [checkCellSelectable, rowIndexByEntityId, rows]
  )

  const selectRowCellsExcept = useCallback(
    (rowId: string, excludedSlotIndex: number) => {
      const cells = getRowCellCoords(rowId, slots.length).filter(
        (cell) =>
          cell.slotIndex !== excludedSlotIndex && checkCellSelectable(cell.entityId, cell.slotIndex)
      )
      const next = new Set(cells.map((c) => toCellKey(c.entityId, c.slotIndex)))

      if (next.size === 0) {
        setSelection({ kind: 'none' })
        return
      }

      const fallbackSlot = excludedSlotIndex === 0 ? 1 : 0
      const firstSelectedKey = Array.from(next)[0]
      if (!firstSelectedKey) {
        setSelection({ kind: 'none' })
        return
      }
      const focusedKey = next.has(toCellKey(rowId, fallbackSlot))
        ? toCellKey(rowId, fallbackSlot)
        : firstSelectedKey
      const focused = fromCellKey(focusedKey)
      setSelection({ kind: 'cells', keys: next, anchor: focused, focused })
    },
    [slots.length, checkCellSelectable]
  )

  const handleCellSelected = useCallback(
    (
      entity: TEntity,
      slotIndex: number,
      modifiers: { shiftKey: boolean; metaKey: boolean; ctrlKey: boolean }
    ) => {
      if (!checkCellSelectable(entity.id, slotIndex)) return

      const cell = { entityId: entity.id, slotIndex }
      const isMetaPressed = modifiers.metaKey || modifiers.ctrlKey
      const anchorCell = selection.kind === 'cells' ? selection.anchor : null

      if (modifiers.shiftKey && !anchorCell) {
        selectSingleCell(cell)
        return
      }

      if (modifiers.shiftKey && anchorCell) {
        addCellRange(anchorCell, cell, isMetaPressed)
        return
      }

      if (isMetaPressed && selectedRowId === entity.id) {
        selectRowCellsExcept(entity.id, slotIndex)
        return
      }

      if (isMetaPressed && selectedColumnIndex === slotIndex) {
        selectColumnCellsExcept(slotIndex, entity.id)
        return
      }

      if (isMetaPressed) {
        toggleCell(cell)
        return
      }

      if (selection.kind === 'cells' && selection.keys.size === 1) {
        const key = toCellKey(entity.id, slotIndex)
        if (selection.keys.has(key)) {
          clearSelection()
          return
        }
      }

      selectSingleCell(cell)
    },
    [
      addCellRange,
      checkCellSelectable,
      clearSelection,
      selectSingleCell,
      selection,
      selectedColumnIndex,
      selectedRowId,
      selectRowCellsExcept,
      selectColumnCellsExcept,
      toggleCell,
    ]
  )

  const handleRowHeaderSelected = useCallback(
    (entity: TEntity) => {
      if (selectedRowId === entity.id) {
        clearSelection()
        return
      }

      if (!entity.selectable) return

      setSelection({ kind: 'row', rowId: entity.id })
    },
    [clearSelection, selectedRowId]
  )

  const handleColumnHeaderSelected = useCallback(
    (columnIndex: number) => {
      if (selectedColumnIndex === columnIndex) {
        clearSelection()
        return
      }

      const selectableEntityIds: string[] = []
      for (const row of rows) {
        if (checkCellSelectable(row.entity.id, columnIndex)) {
          selectableEntityIds.push(row.entity.id)
        }
      }

      if (selectableEntityIds.length === 0) return

      if (selectableEntityIds.length === rows.length) {
        selectColumn(columnIndex)
        return
      }

      const keys = new Set(selectableEntityIds.map((id) => toCellKey(id, columnIndex)))
      const firstEntityId = selectableEntityIds[0]
      if (!firstEntityId) return
      const anchor = { entityId: firstEntityId, slotIndex: columnIndex }

      setSelection((prev) => {
        if (
          prev.kind === 'cells' &&
          prev.keys.size === keys.size &&
          [...keys].every((k) => prev.keys.has(k))
        ) {
          return { kind: 'none' }
        }
        return { kind: 'cells', keys, anchor, focused: anchor }
      })
    },
    [clearSelection, selectColumn, selectedColumnIndex, checkCellSelectable, rows]
  )

  const toEventKey = useCallback((entityId: string, eventId: string) => {
    return `${entityId}:${eventId}`
  }, [])

  const selectedEventKeys = useMemo(() => {
    return selection.kind === 'event' ? new Set(selection.keys) : new Set<string>()
  }, [selection])

  const handleEventSelected = useCallback(
    (
      entityId: string,
      eventId: string,
      modifiers: { shiftKey: boolean; metaKey: boolean; ctrlKey: boolean }
    ) => {
      if (!checkSelectable(entityId)) return

      const key = toEventKey(entityId, eventId)
      const isMetaPressed = modifiers.metaKey || modifiers.ctrlKey

      if (isMetaPressed) {
        setSelection((prev) => {
          const next = prev.kind === 'event' ? new Set(prev.keys) : new Set<string>()

          if (next.has(key)) {
            next.delete(key)
          } else {
            next.add(key)
          }

          if (next.size === 0) {
            return { kind: 'none' }
          }

          return { kind: 'event', keys: next }
        })
        return
      }
      setSelection((prev) => {
        if (prev.kind === 'event' && prev.keys.size === 1 && prev.keys.has(key)) {
          return { kind: 'none' }
        }

        return { kind: 'event', keys: new Set([key]) }
      })
    },
    [checkSelectable, toEventKey]
  )

  const hasSelectableRows = useMemo(() => rows.some((r) => r.entity.selectable !== false), [rows])

  const canSelectAll = hasSelectableRows && slots.length > 0

  const isAllSelected = selection.kind === 'all'

  const handleCornerCellSelected = useCallback(() => {
    setSelection((prev) => {
      if (!canSelectAll) return { kind: 'none' }
      return prev.kind === 'all' ? { kind: 'none' } : { kind: 'all' }
    })
  }, [canSelectAll])

  return {
    selection,
    selectedCellKeys,
    selectedRowId,
    selectedColumnIndex,
    selectedFocusedCell,
    selectedEventKeys,
    clearSelection,
    handleCellSelected,
    handleRowHeaderSelected,
    handleColumnHeaderSelected,
    handleCornerCellSelected,
    isAllSelected,
    handleEventSelected,
    fromCellKey,
  }
}

export { useGridSelection }
